import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X, UserPlus } from "lucide-react";
import { sendPost } from "@/api/base";
import {
  useWorkspaceMembers,
  useDeleteWorkspaceMember,
} from "@/api/workspace/queries";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Member,
  PermissionLevel,
  WORKSPACE_MEMBERS,
} from "@/api/workspace/types";
import { getRoleByValue, roleOptions } from "@/constants/members";
import { toast } from "sonner";
import { useLimits } from "@/hooks/useLimits";
import ShopifyBillingButton from "@/components/billing/ShopifyBillingButton";

export default function Component() {
  const { data: members = [] } = useWorkspaceMembers();
  const queryClient = useQueryClient();
  const deleteWorkspaceMemberMutation = useDeleteWorkspaceMember();
  const { limitValue: maxSeats, isLimitReached: limitReached } =
    useLimits("seats");

  const addMemberMutation = useMutation({
    mutationFn: (memberData: Partial<Member>) =>
      sendPost("workspace/members/", memberData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WORKSPACE_MEMBERS });
    },
    onError: (error) => {
      toast.error("This user already exists in the workspace");
    },
  });

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<Member | null>(null);
  const [newMember, setNewMember] = useState<Partial<Member>>({});

  const addMember = async (event: React.FormEvent) => {
    event.preventDefault();

    if (newMember.name && newMember.email && !limitReached) {
      const memberData = {
        name: newMember.name,
        email: newMember.email,
        role: newMember.role || PermissionLevel.SA,
        avatar_id: null,
      };

      try {
        await addMemberMutation.mutateAsync(memberData);
        setNewMember({});
        setIsAddModalOpen(false);
      } catch (err: any) {
        console.error("Error adding member", err);

        console.error("Response data:", err.response?.data);
      }
    }
  };

  const removeMember = async () => {
    if (memberToDelete) {
      try {
        await deleteWorkspaceMemberMutation.mutateAsync(memberToDelete.id);
        setMemberToDelete(null);
        setIsDeleteModalOpen(false);
      } catch (err) {
        console.error("Error removing member:", err);
      }
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <span className="text-sm text-muted-foreground">
          {members.length} / {maxSeats} members
        </span>
        <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
          <DialogTrigger asChild>
            {limitReached ? (
              <ShopifyBillingButton>
                <UserPlus className="mr-2 h-4 w-4" />
                Upgrade to add more
              </ShopifyBillingButton>
            ) : (
              <Button>
                <UserPlus className="mr-2 h-4 w-4" />
                Add Member
              </Button>
            )}
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Member</DialogTitle>
              <DialogDescription>
                Add a new member to your workspace here.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={addMember}>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="name" className="text-right">
                    Name
                  </Label>
                  <Input
                    id="name"
                    value={newMember.name || ""}
                    onChange={(e) =>
                      setNewMember({ ...newMember, name: e.target.value })
                    }
                    className="col-span-3"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="email" className="text-right">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={newMember.email || ""}
                    onChange={(e) =>
                      setNewMember({ ...newMember, email: e.target.value })
                    }
                    className="col-span-3"
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="role" className="text-right">
                    Role
                  </Label>
                  <Select
                    defaultValue={PermissionLevel.SA}
                    onValueChange={(value) =>
                      setNewMember({
                        ...newMember,
                        role: value as PermissionLevel,
                      })
                    }>
                    <SelectTrigger className="col-span-3">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roleOptions
                        .filter((role) => role.value !== PermissionLevel.OWNER)
                        .map((role) => (
                          <SelectItem
                            key={`${role.value}-${role.label}`}
                            value={role.value}>
                            {role.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit">Add Member</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <ul className="space-y-4">
        {members.map((member) => (
          <li
            key={member.id}
            className="flex items-center justify-between bg-card p-4 rounded-lg shadow">
            <div className="flex items-center space-x-4">
              <Avatar>
                <AvatarImage
                  src={member.avatar || undefined}
                  alt={member.name}
                />
                <AvatarFallback>
                  {member.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </AvatarFallback>
              </Avatar>
              <div>
                <h2 className="font-semibold">{member.name}</h2>
                <p className="text-sm text-muted-foreground">{member.email}</p>
                <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                  {getRoleByValue(member.role)}
                </span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setMemberToDelete(member);
                setIsDeleteModalOpen(true);
              }}>
              <X className="h-4 w-4" />
              <span className="sr-only">Remove member</span>
            </Button>
          </li>
        ))}
      </ul>
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove {memberToDelete?.name} from the
              workspace?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={removeMember}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
